import React from 'react';

import { Button, ChipProps, IconButton, InputAdornment, TextField, TextFieldProps } from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {   
    geocodeByAddress,
    geocodeByPlaceId,
    getLatLng
} from 'react-places-autocomplete';

import * as Elevated from '@pitaman71/omniglot-introspect';
import { Clients, Streams, Values } from '@pitaman71/omniglot-live-data';
import { Domains } from '@pitaman71/omniglot-live-domains';


import * as Styler from './Styler';
import { Propose } from '..';

type WhereType = Elevated.getValueType<typeof Domains.Logistics.WhereDomain>;

export function AsLocationButton(props: {
    active?: boolean,
    domain?: Elevated.Domain<Partial<Values.Parseable<WhereType>>>,
    value?: Partial<Values.Parseable<WhereType>>,
    client?: Clients.ScalarClient<Partial<Values.Parseable<WhereType>>>
}) {  
    return (
        <Button onClick={props.active === false ? undefined : (event) => { 
            const lat = props.value?.parsed?.geo?.point?.lat || props.value?.parsed?.geo?.point?.lat;
            const lng = props.value?.parsed?.geo?.point?.lng || props.value?.parsed?.geo?.point?.lng;
            window.open(`https://maps.google.com?q=${lat},${lng}`); 
        } }>{props.value?.text}</Button>
    )  
}


export function AsLocationInput(props: {
    active?: boolean,
    chipProps?: ChipProps,
    inputProps?: TextFieldProps,
    domain?: Elevated.Domain<Partial<Values.Parseable<WhereType>>>,
    value?: Partial<Values.Parseable<WhereType>>,
    client?: Clients.ScalarClient<Partial<Values.Parseable<WhereType>>>
}) {  
    const [ suggestions, setSuggestions ] = React.useState<string[]>([]);
    const[ error, setError ] = React.useState<string|undefined>(undefined);
    const [ loading, setLoading ] = React.useState(false);
    return <Autocomplete
        id="tags-filled"
        options={suggestions}
        value={loading ? null : props.value?.text || ""}
        freeSolo
        autoSelect={true}
        renderTags={(value, getTagProps) =>
            value.map((option, index) => (
                <Styler.Chip {...props.chipProps} label={option} {...getTagProps({ index })} />
            ))
        }
        onChange={props.active === false ? undefined : (event, value) => {
            props.client?.assign({ 
                text: value || ""
            });
        }}
        renderInput={(params) => (
            <TextField 
                {...params} 
                InputProps={{
                    startAdornment: <InputAdornment position="start"><_AsCurrentLocation {...props} setLoading={setLoading} setError={setError}/></InputAdornment>,
                }}        
                {...props.inputProps}
                placeholder={loading ? 'loading ...' : props.inputProps?.placeholder}
                helperText={error}
                onChange={loading ? undefined : (event:any) => { 
                    props.client?.assign({ 
                        text: event.target.value
                    });
                    geocodeByAddress(event.target.value).then(geocodes => {
                        setSuggestions(geocodes.map(geocode => {
                            return geocode.formatted_address;
                        }));
                        const place_id = geocodes[0].place_id;
                        return getLatLng(geocodes[0])
                        .then(geo => props.client?.assign({ 
                            text: event.target.value, 
                            parsed: { 
                                googlePlaceId: place_id,
                                address: {},
                                geo: { point: geo } 
                            }
                        }));
                    }).catch(err => {
                        setError(`${err}`);
                    });
                }}                
            />
        )}
    />
}

function _AsCurrentLocation(props: {
    active?: boolean,
    icon?: JSX.Element,
    setLoading?: React.Dispatch<boolean>,
    setError?: React.Dispatch<string>,
    client?: Clients.ScalarClient<Partial<Values.Parseable<WhereType>>>
}) {  
    return (
        <Styler.IconButton onClick={props.active === false ? undefined : 
            () => {
                props.setLoading && props.setLoading(true);
                navigator.geolocation.getCurrentPosition((position) => {
                    geocodeByAddress(`${position.coords.latitude}, ${position.coords.longitude}`).then(geocodes => {
                        const place_id = geocodes[0].place_id;
                        return geocodeByPlaceId(place_id)
                        .then(matches => {
                            if(matches.length > 0) {
                                const place = matches[0];
                                let locality: string[] = [];
                                let address: Partial<Elevated.getValueType<typeof Domains.Logistics.AddressDomain>> = {};
                                for (var i = 0; i < place.address_components.length; i++) {
                                    for (var j = 0; j < place.address_components[i].types.length; j++) {
                                        if (matches[0].address_components[i].types[j] == "locality") {
                                            locality.push(place.address_components[i].long_name);
                                            address.city = { 
                                                shortName: place.address_components[i].short_name,
                                                longName: place.address_components[i].long_name
                                            };
                                        }
                                    }
                                }
                                for (var i = 0; i < place.address_components.length; i++) {
                                    for (var j = 0; j < place.address_components[i].types.length; j++) {
                                        if (matches[0].address_components[i].types[j] == "administrative_area_level_1") {
                                            locality.push(place.address_components[i].short_name);
                                            address.state = { 
                                                longName: place.address_components[i].long_name,
                                                shortName: place.address_components[i].short_name 
                                            };
                                        }
                                    }
                                }
                                for (var i = 0; i < place.address_components.length; i++) {
                                    for (var j = 0; j < place.address_components[i].types.length; j++) {
                                        if (matches[0].address_components[i].types[j] == "country") {
                                            locality.push(place.address_components[i].short_name);
                                            address.country = { 
                                                shortName: place.address_components[i].short_name,
                                                longName: place.address_components[i].long_name 
                                            };
                                        }
                                    }
                                }
                                
                                props.client?.assign({ 
                                    text: locality.length > 0 ? locality.join(', ') : `${position.coords.latitude}, ${position.coords.longitude}`, 
                                    parsed: { 
                                        googlePlaceId: place_id,
                                        address,
                                        geo: { point: { 
                                            lat: position.coords.latitude,
                                            lng: position.coords.longitude
                                        } } 
                                    }
                                });
                                props.setLoading && props.setLoading(false);
                            }
                        })
                    }).catch(err => {
                        props.setError && props.setError(`${err}`);
                        props.setLoading && props.setLoading(false);
                    });
                }, (err) => console.error(err))
            }
        }>{ props.icon || <MuiIcons.MyLocationOutlined/> }</Styler.IconButton>
    )
}
